import type { Feature } from "@/lib/planFeatures";

/**
 * Üyelik bitiminden sonra opsiyonel "ek süre" (grace period).
 *
 * Ayarlar `site_settings` tablosunda tutulur:
 *   - grace_period_days      → "0" ise kapalı, ">0" ise gün sayısı
 *   - grace_allowed_features → JSON dizi, bu süre boyunca açık kalan yetkiler
 *   - grace_banner_enabled   → "true"/"false", uyarı şeridi gösterimi
 */

export const GRACE_SETTING_KEYS = {
  days: "grace_period_days",
  features: "grace_allowed_features",
  banner: "grace_banner_enabled",
} as const;

/** Ek süre boyunca açılmasına izin verilebilecek yetkiler. */
export const GRACE_ELIGIBLE_FEATURES: Feature[] = [
  "diet_premium",
  "videos_all",
  "training_premium",
  "appointments",
  "ai_chat",
  "ai_analysis",
  "priority_support",
];

export const DEFAULT_GRACE_FEATURES: Feature[] = [
  "diet_premium",
  "ai_chat",
  "priority_support",
];

export interface GraceConfig {
  /** 0 → grace period kapalı. */
  days: number;
  allowedFeatures: Feature[];
  bannerEnabled: boolean;
}

export const DEFAULT_GRACE_CONFIG: GraceConfig = {
  days: 0,
  allowedFeatures: DEFAULT_GRACE_FEATURES,
  bannerEnabled: true,
};

const isFeature = (v: unknown): v is Feature =>
  typeof v === "string" && (GRACE_ELIGIBLE_FEATURES as string[]).includes(v);

export const parseGraceFeatures = (raw: string | null | undefined): Feature[] => {
  if (!raw) return DEFAULT_GRACE_FEATURES;
  try {
    const parsed = JSON.parse(raw);
    if (Array.isArray(parsed)) return parsed.filter(isFeature);
  } catch {
    // virgülle ayrılmış eski format desteği
    const parts = raw.split(",").map((p) => p.trim());
    const filtered = parts.filter(isFeature);
    if (filtered.length) return filtered;
  }
  return [];
};

export const parseGraceConfig = (
  settings: Record<string, string | null | undefined>,
): GraceConfig => {
  const rawDays = settings[GRACE_SETTING_KEYS.days];
  const days = Number.parseInt(rawDays ?? "", 10);
  return {
    days: Number.isFinite(days) && days > 0 ? Math.min(days, 90) : 0,
    allowedFeatures: parseGraceFeatures(settings[GRACE_SETTING_KEYS.features]),
    bannerEnabled: (settings[GRACE_SETTING_KEYS.banner] ?? "true") !== "false",
  };
};

export interface GraceWindow {
  /** Şu an ek süre içinde miyiz? */
  active: boolean;
  /** Ek sürenin bitiş anı (ISO). */
  endsAt: string | null;
  /** Kalan tam gün sayısı (yukarı yuvarlanır). */
  daysLeft: number;
  /** Ek süreye kaynaklık eden üyeliğin bitiş tarihi (ISO). */
  expiredAt: string | null;
}

export const NO_GRACE: GraceWindow = {
  active: false,
  endsAt: null,
  daysLeft: 0,
  expiredAt: null,
};

/**
 * Süresi dolmuş üyelik bitiş tarihlerinden ek süre penceresini hesaplar.
 * En geç biten üyelik esas alınır.
 */
export const computeGraceWindow = (
  endedAtList: ReadonlyArray<string | null | undefined>,
  config: GraceConfig,
  now: Date = new Date(),
): GraceWindow => {
  if (config.days <= 0) return NO_GRACE;
  let latest: number | null = null;
  for (const iso of endedAtList) {
    if (!iso) continue;
    const t = new Date(iso).getTime();
    if (!Number.isFinite(t)) continue;
    if (latest === null || t > latest) latest = t;
  }
  if (latest === null) return NO_GRACE;
  const nowMs = now.getTime();
  if (latest > nowMs) return NO_GRACE; // üyelik hâlâ sürüyor
  const endsAtMs = latest + config.days * 86_400_000;
  if (endsAtMs <= nowMs) return NO_GRACE; // ek süre de doldu
  return {
    active: true,
    endsAt: new Date(endsAtMs).toISOString(),
    daysLeft: Math.max(1, Math.ceil((endsAtMs - nowMs) / 86_400_000)),
    expiredAt: new Date(latest).toISOString(),
  };
};

/** Ek süre boyunca bu yetki açık mı? */
export const graceAllows = (config: GraceConfig, feature: Feature): boolean =>
  config.allowedFeatures.includes(feature);
